import React, { ReactElement } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import { UserScreenId } from "@vahak/core/dist/constants";

import { PlainLayout } from "../../components/layout/plain-layout/PlainLayout";
import LcvRouteLandingPage from "../../components/lcv-route-landing/LcvRouteLandingPage";
import { getLcvRouteBySlug, getLcvRouteSlugs } from "../../components/lcv-route-landing/data";
import { LcvRouteConfig } from "../../components/lcv-route-landing/data/types";
import { getRouteLocalizationNamespaces } from "../../methods/getRouteLocalizationNamespaces";

interface LcvRouteBookingPageProps {
    routeConfig: LcvRouteConfig;
}

export default function LcvRouteBookingPage({ routeConfig }: LcvRouteBookingPageProps) {
    return <LcvRouteLandingPage routeConfig={routeConfig} />;
}

LcvRouteBookingPage.getLayout = (page: ReactElement) => <PlainLayout page={page} />;

export const getStaticPaths: GetStaticPaths = async () => {
    const paths = getLcvRouteSlugs().flatMap((routeSlug) => {
        const routeConfig = getLcvRouteBySlug(routeSlug);
        const locales = routeConfig?.hreflangLocales ?? ["en"];

        return locales.map((locale) => ({
            params: { routeSlug },
            locale
        }));
    });

    return {
        paths,
        fallback: false
    };
};

export const getStaticProps: GetStaticProps<LcvRouteBookingPageProps> = async ({ params, locale }) => {
    const routeSlug = params?.routeSlug;

    if (typeof routeSlug !== "string") {
        return {
            notFound: true
        };
    }

    const routeConfig = getLcvRouteBySlug(routeSlug);

    if (!routeConfig) {
        return {
            notFound: true
        };
    }

    const supportedLocales = routeConfig.hreflangLocales ?? ["en"];

    if (!locale || !supportedLocales.includes(locale)) {
        return {
            notFound: true
        };
    }

    return {
        props: {
            route: UserScreenId.lcvBooking.id,
            routeConfig,
            ...(await serverSideTranslations(
                locale || "en",
                getRouteLocalizationNamespaces(UserScreenId.lcvBooking.pathname)
            ))
        }
    };
};
